"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Search, Target, MapPin, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, Compass, Building2, Sparkles, Zap, ShieldCheck, Award } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid } from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const keywordsData = [{
  id: 1,
  term: "SEO consultant Wolverhampton",
  volume: "1,200/mo",
  intent: "High Commercial",
  difficulty: "Medium",
  opportunity: "Direct access to high-intent regional decision-makers looking for expert consultancy."
}, {
  id: 2,
  term: "B2B services Wolverhampton",
  volume: "3,400/mo",
  intent: "Commercial Lead Gen",
  difficulty: "Low-Medium",
  opportunity: "Industrial and logistics hubs seeking direct supplier & partner digital visibility."
}, {
  id: 3,
  term: "Engineering SEO West Midlands",
  volume: "890/mo",
  intent: "Transactional",
  difficulty: "Low",
  opportunity: "Uncontested niche dominance for regional manufacturing & specialized firms."
}, {
  id: 4,
  term: "Ecommerce SEO Wolverhampton",
  volume: "2,100/mo",
  intent: "High Commercial",
  difficulty: "Medium",
  opportunity: "Local retail brands transitioning to national organic sales channels."
}];
const neighboringLocations = [{
  id: 1,
  city: "Stoke on Trent",
  distance: "32 miles north",
  path: "/LocalSeoConsultantStokeOnTrentPage",
  highlight: "Ceramics, industrial supply chain & B2B search trends"
}, {
  id: 2,
  city: "Derby",
  distance: "45 miles northeast",
  path: "/LocalSeoConsultantDerbyPage",
  highlight: "High-tech manufacturing, aerospace & engineering growth"
}, {
  id: 3,
  city: "Northampton",
  distance: "58 miles southeast",
  path: "/LocalSeoConsultantNorthamptonPage",
  highlight: "Logistics corridor, enterprise & retail market opportunities"
}, {
  id: 4,
  city: "Milton Keynes",
  distance: "70 miles southeast",
  path: "/LocalSeoConsultantMiltonKeynesPage",
  highlight: "Scale-ups, innovation tech & professional services SEO"
}, {
  id: 5,
  city: "Wakefield",
  distance: "85 miles north",
  path: "/LocalSeoConsultantWakefieldPage",
  highlight: "Regional logistics, trade & B2B expansion opportunities"
}];
const trendChartData = [{
  month: "Jan",
  searchDemand: 3200,
  opportunityScore: 78
}, {
  month: "Feb",
  searchDemand: 3500,
  opportunityScore: 82
}, {
  month: "Mar",
  searchDemand: 4100,
  opportunityScore: 85
}, {
  month: "Apr",
  searchDemand: 4800,
  opportunityScore: 88
}, {
  month: "May",
  searchDemand: 5400,
  opportunityScore: 91
}, {
  month: "Jun",
  searchDemand: 6200,
  opportunityScore: 95
}];
const chartConfig: ChartConfig = {
  searchDemand: {
    label: "Wolverhampton Organic Search Demand",
    color: "hsl(var(--primary))"
  },
  opportunityScore: {
    label: "Growth Index Score",
    color: "hsl(var(--accent))"
  }
};
const marketInsights = [{
  id: 1,
  title: "High Industrial & B2B Concentration",
  description: "Wolverhampton businesses frequently rely on regional B2B contracts. Local search optimization yields direct inbound leads without expensive PPC spend."
}, {
  id: 2,
  title: "Low Agency Sophistication in Local SERPs",
  description: "Most local competitors rely on generic template agencies with outdated tactics. My tailored, hands-on strategy captures premium rankings rapidly."
}, {
  id: 3,
  title: "AI Search & Geo-Targeting Shift",
  description: "Google's AI Overviews and local map packs prioritize structured entities. I align your business directly with localized AI search discovery."
}];
export default function Wolverhamptonmarketanalysis() {
  const prefersReducedMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<"overview" | "keywords" | "competitors">("overview");
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="wolverhamptonmarketanalysis" data-nav="light" className="relative w-full bg-background py-16 md:py-24 overflow-hidden text-foreground">
      {}
      <div className="pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-primary/5 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 -left-24 size-96 rounded-full bg-accent/10 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={containerVariants} className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <motion.div variants={itemVariants} className="flex justify-center mb-4">
            <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5 rounded-full border border-border">
              <MapPin className="size-3.5 text-primary" />
              Regional Intelligence
            </Badge>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-serif">
            Wolverhampton Search Market Analysis &amp; Growth Opportunities
          </motion.h1>

          <motion.p variants={itemVariants} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            I conduct granular, data-backed local market audits to reveal exactly how Wolverhampton customers search for your services. You work directly with me to turn untapped commercial search volume into sustainable revenue.
          </motion.p>
        </motion.div>

        {}
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-lg bg-muted p-1 border border-border">
            <Button variant={activeTab === "overview" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("overview")} className="rounded-md px-4 py-2 text-xs sm:text-sm font-medium transition-[background-color,color]">
              <BarChart3 className="size-4 mr-2" />
              Market Demand
            </Button>
            <Button variant={activeTab === "keywords" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("keywords")} className="rounded-md px-4 py-2 text-xs sm:text-sm font-medium transition-[background-color,color]">
              <Target className="size-4 mr-2" />
              Key Keyword Clusters
            </Button>
            <Button variant={activeTab === "competitors" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("competitors")} className="rounded-md px-4 py-2 text-xs sm:text-sm font-medium transition-[background-color,color]">
              <Building2 className="size-4 mr-2" />
              Competitive Advantage
            </Button>
          </div>
        </div>

        {}
        {activeTab === "overview" && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
            <Card className="lg:col-span-7 bg-card border-border shadow-md">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl font-bold font-serif flex items-center gap-2">
                      <TrendingUp className="size-5 text-primary" />
                      Wolverhampton Commercial Search Trajectory
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-1">
                      Aggregated monthly organic queries across key regional business sectors
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="hidden sm:inline-flex bg-accent/10 text-foreground border-accent/30">
                    Up +42% YOY
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <ChartContainer config={chartConfig} className="h-72 w-full">
                  <AreaChart data={trendChartData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }}>
                    <defs>
                      <linearGradient id="fillDemand" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="fillScore" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-accent)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/50" />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} className="text-xs text-muted-foreground" />
                    <YAxis tickLine={false} axisLine={false} className="text-xs text-muted-foreground" />
                    <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
                    <Area type="monotone" dataKey="searchDemand" stroke="var(--color-primary)" strokeWidth={2} fillOpacity={1} fill="url(#fillDemand)" />
                    <Area type="monotone" dataKey="opportunityScore" stroke="var(--color-accent)" strokeWidth={2} fillOpacity={1} fill="url(#fillScore)" />
                  </AreaChart>
                </ChartContainer>
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t border-border mt-2">
                  <div className="flex items-center gap-2">
                    <span className="size-2.5 rounded-full bg-primary" />
                    <span>Search Demand Volume</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="size-2.5 rounded-full bg-accent" />
                    <span>Conversion Opportunity Index</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="lg:col-span-5 space-y-4">
              {marketInsights.map((insight, i) => <Card data-index={i} key={insight.id} className="bg-card border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
                  <CardHeader className="p-5 pb-2">
                    <CardTitle className="text-base font-semibold font-serif flex items-center gap-2">
                      <Sparkles className="size-4 text-accent" />
                      {insight.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-5 pb-5 pt-0 text-sm text-muted-foreground leading-relaxed">
                    {insight.description}
                  </CardContent>
                </Card>)}
            </div>
          </motion.div>}

        {}
        {activeTab === "keywords" && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {keywordsData.map((kw, i) => <Card data-index={i} key={kw.id} className="bg-card border-border shadow-md hover:shadow-lg transition-[border-color,box-shadow]">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <Badge variant="outline" className="mb-2 text-primary border-primary/30">
                        {kw.intent}
                      </Badge>
                      <CardTitle className="text-lg font-bold font-serif">{kw.term}</CardTitle>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-muted-foreground block">Monthly Volume</span>
                      <span className="text-sm font-semibold text-foreground font-mono">{kw.volume}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between text-xs py-1 border-y border-border">
                    <span className="text-muted-foreground">Ranking Difficulty:</span>
                    <span className="font-medium text-foreground">{kw.difficulty}</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    <strong className="text-foreground font-medium">Strategic Value:</strong> {kw.opportunity}
                  </p>
                </CardContent>
              </Card>)}
          </motion.div>}

        {}
        {activeTab === "competitors" && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="mb-16">
            <Card className="bg-card border-border shadow-md overflow-hidden">
              <CardHeader className="bg-muted/50 border-b border-border">
                <CardTitle className="text-xl font-bold font-serif">
                  Traditional SEO Agency vs. Direct Freelance SEO Consultant
                </CardTitle>
                <CardDescription>
                  Why Wolverhampton businesses achieve faster, higher ROI when working directly with Yassine Essaadi
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
                  <div className="p-6 space-y-4 bg-muted/20">
                    <h3 className="font-semibold text-destructive flex items-center gap-2">
                      Standard Local Agencies
                    </h3>
                    <ul className="space-y-3 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-destructive font-bold text-base line-through">×</span>
                        <span>Junior account managers handling 30+ client campaigns simultaneously.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-destructive font-bold text-base line-through">×</span>
                        <span>Generic, auto-generated PDF audits that lack business context.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-destructive font-bold text-base line-through">×</span>
                        <span>Slow response times with zero direct technical execution capability.</span>
                      </li>
                    </ul>
                  </div>

                  <div className="p-6 space-y-4 bg-primary/5">
                    <h3 className="font-semibold text-primary flex items-center gap-2">
                      <ShieldCheck className="size-5 text-primary" />
                      Direct Access to Yassine Essaadi
                    </h3>
                    <ul className="space-y-3 text-sm text-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>You work directly with me—no middlemen, sub-contractors, or junior reps.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>Bespoke SEO strategy aligned specifically with Wolverhampton buyer intent.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>Hands-on technical implementation, transparent reporting, and direct contact.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>}

        {}
        <div className="mt-16 pt-12 border-t border-border">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <Badge variant="outline" className="mb-2 text-primary border-primary/30">
                <Compass className="size-3.5 mr-1" />
                West Midlands &amp; Neighboring Hubs
              </Badge>
              <h2 className="text-2xl font-bold font-serif tracking-tight">
                Explore Neighboring Regional Search Markets
              </h2>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Expanding your digital footprint across surrounding commercial hubs? Review my localized search analyses for neighboring cities.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {neighboringLocations.map((loc, i) => <Card data-index={i} key={loc.id} className="bg-card border-border hover:border-primary/50 shadow-xs hover:shadow-md transition-[border-color,box-shadow] group">
                <CardContent className="p-5 flex flex-col justify-between h-full">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors flex items-center gap-1.5">
                        <MapPin className="size-4 text-primary" />
                        {loc.city}
                      </h3>
                      <span className="text-xs text-muted-foreground font-mono">{loc.distance}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                      {loc.highlight}
                    </p>
                  </div>

                  <Button asChild variant="ghost" size="sm" className="w-full justify-between hover:bg-primary/10 text-primary">
                    <Link to={loc.path}>
                      <span>View {loc.city} Market Data</span>
                      <ArrowRight className="size-3.5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>)}

            <Card className="bg-primary/5 border-primary/20 shadow-xs flex flex-col justify-between">
              <CardContent className="p-5 flex flex-col justify-between h-full">
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Zap className="size-4 text-primary" />
                    <h3 className="font-semibold text-foreground">National &amp; Multi-Location SEO</h3>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                    Need full UK-wide organic coverage across multiple Midlands or national territories?
                  </p>
                </div>
                <Button asChild variant="default" size="sm" className="w-full justify-between">
                  <Link to="/LocalSEOPage">
                    <span>Local SEO Strategy</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {}
        <div className="mt-16 rounded-2xl bg-primary text-primary-foreground p-8 md:p-12 shadow-xl relative overflow-hidden">
          <div className="pointer-events-none absolute -right-10 -bottom-10 size-64 rounded-full bg-white/10 blur-2xl" />
          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl text-center lg:text-left">
              <Badge variant="secondary" className="mb-3 bg-white/10 text-white hover:bg-white/20 border-white/20">
                Direct Consultation
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold font-serif tracking-tight text-white">
                Ready to dominate Wolverhampton search results?
              </h2>
              <p className="mt-2 text-primary-foreground/90 text-sm sm:text-base leading-relaxed">
                Book a free, direct SEO consultation with me. I will manually review your site, map your local competitors, and outline a realistic growth roadmap.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 shrink-0 w-full sm:w-auto">
              <Button asChild size="lg" variant="secondary" className="font-semibold shadow-md rounded-md">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 bg-transparent rounded-md">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}